import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { logsAPI } from '@/services/api';
import type { UsageLog, UsageLogsStats } from '@/types/domain';

export interface LogFiltersValue {
  action?: string;
  startDate: Date | null;
  endDate: Date | null;
  ipAddress?: string;
  licenseKey?: string;
}

export interface UseLogsReturn {
  logs: UsageLog[];
  stats: UsageLogsStats | null;
  loading: boolean;
  statsLoading: boolean;
  error: string | null;
  success: string | null;
  totalCount: number;
  paginationModel: { page: number; pageSize: number };
  setPaginationModel: (model: { page: number; pageSize: number }) => void;
  filters: LogFiltersValue;
  activeFilters: LogFiltersValue;
  setFilters: (filters: LogFiltersValue) => void;
  applyFilters: () => void;
  clearFilters: () => void;
  refresh: () => void;
  export: () => Promise<void>;
  exporting: boolean;
  setSuccess: (message: string) => void;
  setError: (message: string) => void;
  clearMessages: () => void;
}

const DEFAULT_FILTERS: LogFiltersValue = {
  startDate: null,
  endDate: null,
};

export function useLogs(): UseLogsReturn {
  const { t } = useTranslation();

  // 数据状态
  const [logs, setLogs] = useState<UsageLog[]>([]);
  const [stats, setStats] = useState<UsageLogsStats | null>(null);
  const [totalCount, setTotalCount] = useState(0);

  // 加载状态
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  // 消息状态
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // 分页状态
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });

  // 过滤器状态
  const [filters, setFilters] = useState<LogFiltersValue>(DEFAULT_FILTERS);
  const [activeFilters, setActiveFilters] = useState<LogFiltersValue>(DEFAULT_FILTERS);

  // 加载日志数据
  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        limit: paginationModel.pageSize,
        offset: paginationModel.page * paginationModel.pageSize,
        ...(activeFilters.action && { action: activeFilters.action }),
        ...(activeFilters.startDate && {
          start_date: format(activeFilters.startDate, 'yyyy-MM-dd'),
        }),
        ...(activeFilters.endDate && {
          end_date: format(activeFilters.endDate, 'yyyy-MM-dd'),
        }),
        ...(activeFilters.ipAddress && { ip_address: activeFilters.ipAddress }),
        ...(activeFilters.licenseKey && { license_key: activeFilters.licenseKey }),
      };

      const response = await logsAPI.getAll(params);
      setLogs(response.data || []);
      setTotalCount(response.total || 0);
    } catch (err: any) {
      setErrorMessage(err.response?.data?.error || t('logs.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [paginationModel.page, paginationModel.pageSize, activeFilters, t]);

  // 加载统计数据
  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const params = {
        ...(activeFilters.startDate && {
          start_date: format(activeFilters.startDate, 'yyyy-MM-dd'),
        }),
        ...(activeFilters.endDate && {
          end_date: format(activeFilters.endDate, 'yyyy-MM-dd'),
        }),
      };

      const response = await logsAPI.getStats(params);
      setStats(response.data);
    } catch (err: any) {
      console.error('Failed to load stats:', err);
    } finally {
      setStatsLoading(false);
    }
  }, [activeFilters]);

  // 初始加载和过滤器变化时重新加载
  useEffect(() => {
    loadLogs();
    loadStats();
  }, [loadLogs, loadStats]);

  // 应用过滤器
  const applyFilters = useCallback(() => {
    setActiveFilters(filters);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [filters]);

  // 清除过滤器
  const clearFilters = useCallback(() => {
    const clearedFilters: LogFiltersValue = DEFAULT_FILTERS;
    setFilters(clearedFilters);
    setActiveFilters(clearedFilters);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  // 刷新数据
  const refresh = useCallback(() => {
    loadLogs();
    loadStats();
    setSuccessMessage(t('logs.refreshSuccess'));
  }, [loadLogs, loadStats, t]);

  // 导出数据
  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const params = {
        ...(activeFilters.action && { action: activeFilters.action }),
        ...(activeFilters.startDate && {
          start_date: format(activeFilters.startDate, 'yyyy-MM-dd'),
        }),
        ...(activeFilters.endDate && {
          end_date: format(activeFilters.endDate, 'yyyy-MM-dd'),
        }),
        ...(activeFilters.ipAddress && { ip_address: activeFilters.ipAddress }),
        ...(activeFilters.licenseKey && { license_key: activeFilters.licenseKey }),
      };

      const response = await logsAPI.export(params);

      // 创建下载链接
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `usage-logs-${new Date().toISOString().split('T')[0]}.csv`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setSuccessMessage(t('logs.exportSuccess'));
    } catch (err: any) {
      setErrorMessage(err.response?.data?.error || t('logs.exportFailed'));
    } finally {
      setExporting(false);
    }
  }, [activeFilters, t]);

  // 消息管理
  const setSuccess = useCallback((message: string) => {
    setSuccessMessage(message);
  }, []);

  const setError = useCallback((message: string) => {
    setErrorMessage(message);
  }, []);

  const clearMessages = useCallback(() => {
    setSuccessMessage(null);
    setErrorMessage(null);
  }, []);

  return {
    logs,
    stats,
    loading,
    statsLoading,
    error: errorMessage,
    success: successMessage,
    totalCount,
    paginationModel,
    setPaginationModel,
    filters,
    activeFilters,
    setFilters,
    applyFilters,
    clearFilters,
    refresh,
    export: handleExport,
    exporting,
    setSuccess,
    setError,
    clearMessages,
  };
}
